// @ts-nocheck
import React, { useState, useEffect } from 'react';

interface CollectionItem {
  _id: string;
  name: string;
  color?: string;
  itemCount?: number;
}

interface AddLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { url: string; note: string; tags: string[]; collectionIds: string[] }) => Promise<any>;
  collections: CollectionItem[];
  defaultCollectionId?: string;
  onOpenCreateCollection?: () => void;
  onCreateCollection?: (data: { name: string; description?: string; color?: string }) => Promise<any>;
  justCreatedCollectionId?: string | null;
}

export default function AddLinkModal({
  isOpen,
  onClose,
  onSubmit,
  collections = [],
  defaultCollectionId,
  onOpenCreateCollection,
  onCreateCollection,
  justCreatedCollectionId,
}: AddLinkModalProps) {
  const [url, setUrl] = useState('');
  const [note, setNote] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [selectedCollectionIds, setSelectedCollectionIds] = useState<string[]>([]);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Inline collection creation state
  const [isCreatingInline, setIsCreatingInline] = useState(false);
  const [inlineCollectionName, setInlineCollectionName] = useState('');
  const [isInlineSubmitting, setIsInlineSubmitting] = useState(false);
  const [collectionFeedback, setCollectionFeedback] = useState('');

  // When modal opens, initialize default collection if provided
  useEffect(() => {
    if (isOpen) {
      if (defaultCollectionId) {
        setSelectedCollectionIds([defaultCollectionId]);
      } else {
        setSelectedCollectionIds([]);
      }
      setErrorMessage('');
      setStatusMessage('');
      setIsCreatingInline(false);
      setInlineCollectionName('');
      setCollectionFeedback('');
    }
  }, [isOpen, defaultCollectionId]);

  useEffect(() => {
    if (justCreatedCollectionId) {
      setSelectedCollectionIds((prev) =>
        prev.includes(justCreatedCollectionId) ? prev : [...prev, justCreatedCollectionId]
      );
    }
  }, [justCreatedCollectionId]);

  if (!isOpen) return null;

  const handleToggleCollection = (colId: string) => {
    setSelectedCollectionIds((prev) =>
      prev.includes(colId) ? prev.filter((id) => id !== colId) : [...prev, colId]
    );
  };

  const handleSelectAllCollections = () => {
    setSelectedCollectionIds(collections.map((c) => c._id));
  };

  const handleClearCollections = () => {
    setSelectedCollectionIds([]);
  };

  const handleInlineAddCollection = async () => {
    const trimmed = inlineCollectionName.trim();
    if (!trimmed) return;

    const existing = collections.find(
      (c) => c.name.toLowerCase() === trimmed.toLowerCase()
    );

    if (existing) {
      setSelectedCollectionIds((prev) =>
        prev.includes(existing._id) ? prev : [...prev, existing._id]
      );
      setInlineCollectionName('');
      setIsCreatingInline(false);
      setCollectionFeedback(`Selected existing "${existing.name}" collection`);
      setTimeout(() => setCollectionFeedback(''), 4000);
      return;
    }

    if (onCreateCollection) {
      setIsInlineSubmitting(true);
      try {
        const created = await onCreateCollection({ name: trimmed });
        const createdId = created?._id || created?.data?._id;
        if (createdId) {
          setSelectedCollectionIds((prev) =>
            prev.includes(createdId) ? prev : [...prev, createdId]
          );
        }
        setInlineCollectionName('');
        setIsCreatingInline(false);
        setCollectionFeedback(`Added and selected "${trimmed}"`);
        setTimeout(() => setCollectionFeedback(''), 4000);
      } catch (err: any) {
        setErrorMessage(err.message || 'Failed to create collection');
      } finally {
        setIsInlineSubmitting(false);
      }
    } else if (onOpenCreateCollection) {
      onOpenCreateCollection();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) {
      setErrorMessage('Please enter a valid URL');
      return;
    }

    setErrorMessage('');
    setIsSubmitting(true);
    setStatusMessage('Saving link…');

    try {
      setTimeout(() => {
        if (isSubmitting) setStatusMessage('Fetching preview…');
      }, 700);

      const tags = tagInput
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);

      await onSubmit({
        url: url.trim(),
        note: note.trim(),
        tags,
        collectionIds: selectedCollectionIds,
      });

      setStatusMessage('Saved to library');
      setTimeout(() => {
        setUrl('');
        setNote('');
        setTagInput('');
        setSelectedCollectionIds([]);
        setStatusMessage('');
        setIsSubmitting(false);
        onClose();
      }, 500);
    } catch (err: any) {
      setIsSubmitting(false);
      setStatusMessage('');
      setErrorMessage(err.message || 'Failed to save link. Please check the URL.');
    }
  };

  return (
    <div className="reader-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="reader-modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '560px' }}
      >
        <div className="reader-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '6px',
                background: 'var(--reader-hover)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--reader-accent)',
              }}
            >
              <i className="fi fi-rr-link-alt" style={{ fontSize: '0.85rem' }} />
            </div>
            <h3 className="reader-modal-title">Save Link to Library</h3>
          </div>
          <button
            onClick={onClose}
            aria-label="Close modal"
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.25rem',
              cursor: 'pointer',
              color: 'var(--reader-text-muted)',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="reader-modal-body" style={{ maxHeight: '72vh', overflowY: 'auto' }}>
            {errorMessage && (
              <div
                style={{
                  padding: '10px 12px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid #ef4444',
                  borderRadius: '6px',
                  color: '#ef4444',
                  fontSize: '0.85rem',
                }}
              >
                {errorMessage}
              </div>
            )}

            {statusMessage && (
              <div
                style={{
                  padding: '10px 12px',
                  background: 'var(--reader-accent-light)',
                  border: '1px solid var(--reader-accent)',
                  borderRadius: '6px',
                  color: 'var(--reader-accent)',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <i className="fi fi-rr-spinner" style={{ animation: 'spin 1s linear infinite' }} />
                <span>{statusMessage}</span>
              </div>
            )}

            {/* URL */}
            <div className="reader-form-group">
              <label className="reader-form-label">URL *</label>
              <input
                type="url"
                className="reader-input"
                placeholder="https://example.com/article, youtube.com, podcast..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                autoFocus
                required
                disabled={isSubmitting}
              />
              <span style={{ fontSize: '0.74rem', color: 'var(--reader-text-subtle)' }}>
                Supports articles, YouTube videos, podcast episodes, tweets, and blog posts.
              </span>
            </div>

            {/* CHOOSE COLLECTIONS */}
            <div className="reader-form-group">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '2px',
                }}
              >
                <div>
                  <label className="reader-form-label" style={{ display: 'block' }}>
                    Choose Collection
                  </label>
                  <span
                    style={{
                      fontSize: '0.74rem',
                      color: 'var(--reader-text-subtle)',
                      display: 'block',
                    }}
                  >
                    Select which collection(s) to add this link to, or leave unselected for Inbox.
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {collections.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={handleSelectAllCollections}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '0.72rem',
                          color: 'var(--reader-accent)',
                          cursor: 'pointer',
                          padding: '2px 4px',
                        }}
                      >
                        Select all
                      </button>
                      <span style={{ fontSize: '0.7rem', color: 'var(--reader-border)' }}>•</span>
                    </>
                  )}
                  {selectedCollectionIds.length > 0 && (
                    <button
                      type="button"
                      onClick={handleClearCollections}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '0.72rem',
                        color: 'var(--reader-text-muted)',
                        cursor: 'pointer',
                        padding: '2px 4px',
                      }}
                    >
                      Clear
                    </button>
                  )}
                  {(onCreateCollection || onOpenCreateCollection) && (
                    <>
                      <span style={{ fontSize: '0.7rem', color: 'var(--reader-border)' }}>•</span>
                      <button
                        type="button"
                        onClick={() => {
                          setIsCreatingInline((prev) => !prev);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '0.72rem',
                          color: 'var(--reader-accent)',
                          cursor: 'pointer',
                          padding: '2px 4px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3px',
                        }}
                      >
                        <i className="fi fi-rr-plus" style={{ fontSize: '0.65rem' }} />
                        <span>New</span>
                      </button>
                    </>
                  )}
                </div>
              </div>

              {collections.length === 0 ? (
                <div
                  style={{
                    padding: '12px 14px',
                    borderRadius: '6px',
                    border: '1px dashed var(--reader-border)',
                    background: 'var(--reader-bg)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <span style={{ fontSize: '0.8rem', color: 'var(--reader-text-muted)' }}>
                    No collections created yet.
                  </span>
                  {(onCreateCollection || onOpenCreateCollection) && (
                    <button
                      type="button"
                      className="reader-btn"
                      style={{ fontSize: '0.75rem', height: '26px', padding: '0 8px' }}
                      onClick={() => setIsCreatingInline(true)}
                    >
                      <i className="fi fi-rr-plus" style={{ fontSize: '0.65rem' }} />
                      <span>Create Collection</span>
                    </button>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(155px, 1fr))',
                    gap: '8px',
                    maxHeight: '160px',
                    overflowY: 'auto',
                    padding: '4px 1px',
                  }}
                >
                  {collections.map((col) => {
                    const isSelected = selectedCollectionIds.includes(col._id);
                    return (
                      <div
                        key={col._id}
                        role="checkbox"
                        aria-checked={isSelected}
                        tabIndex={0}
                        onClick={() => handleToggleCollection(col._id)}
                        onKeyDown={(e) => {
                          if (e.key === ' ' || e.key === 'Enter') {
                            e.preventDefault();
                            handleToggleCollection(col._id);
                          }
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: isSelected
                            ? '1.5px solid var(--reader-accent)'
                            : '1px solid var(--reader-border)',
                          background: isSelected
                            ? 'var(--reader-accent-light)'
                            : 'var(--reader-bg)',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          userSelect: 'none',
                        }}
                      >
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '4px',
                            border: isSelected
                              ? '1.5px solid var(--reader-accent)'
                              : '1.5px solid var(--reader-text-subtle)',
                            background: isSelected ? 'var(--reader-accent)' : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ffffff',
                            fontSize: '0.65rem',
                            flexShrink: 0,
                          }}
                        >
                          {isSelected && <i className="fi fi-rr-check" />}
                        </div>

                        <span
                          style={{
                            width: '9px',
                            height: '9px',
                            borderRadius: '2px',
                            backgroundColor: col.color || 'var(--reader-accent)',
                            flexShrink: 0,
                          }}
                        />

                        <span
                          style={{
                            fontSize: '0.82rem',
                            fontWeight: isSelected ? 600 : 400,
                            color: isSelected ? 'var(--reader-accent)' : 'var(--reader-text)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                          }}
                          title={col.name}
                        >
                          {col.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {isCreatingInline && (
                <div
                  style={{
                    marginTop: '8px',
                    padding: '8px 10px',
                    background: 'var(--reader-hover)',
                    border: '1px solid var(--reader-border)',
                    borderRadius: '6px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                  }}
                >
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <input
                      type="text"
                      className="reader-input"
                      style={{ flex: 1, height: '30px', fontSize: '0.82rem', padding: '4px 8px' }}
                      placeholder="Collection name (e.g. Technology)..."
                      value={inlineCollectionName}
                      onChange={(e) => setInlineCollectionName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleInlineAddCollection();
                        } else if (e.key === 'Escape') {
                          setIsCreatingInline(false);
                          setInlineCollectionName('');
                        }
                      }}
                      autoFocus
                      disabled={isInlineSubmitting}
                    />
                    <button
                      type="button"
                      className="reader-btn reader-btn-primary"
                      style={{ height: '30px', fontSize: '0.78rem', padding: '0 10px' }}
                      onClick={handleInlineAddCollection}
                      disabled={isInlineSubmitting || !inlineCollectionName.trim()}
                    >
                      {isInlineSubmitting ? 'Adding…' : 'Add'}
                    </button>
                    <button
                      type="button"
                      className="reader-btn"
                      style={{ height: '30px', fontSize: '0.78rem', padding: '0 8px' }}
                      onClick={() => {
                        setIsCreatingInline(false);
                        setInlineCollectionName('');
                      }}
                      disabled={isInlineSubmitting}
                    >
                      Cancel
                    </button>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.71rem', color: 'var(--reader-text-subtle)' }}>
                      Enter a new or existing collection name.
                    </span>
                    {onOpenCreateCollection && (
                      <button
                        type="button"
                        onClick={onOpenCreateCollection}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '0.71rem',
                          color: 'var(--reader-accent)',
                          cursor: 'pointer',
                          padding: 0,
                          textDecoration: 'underline',
                        }}
                      >
                        More options (color, description)
                      </button>
                    )}
                  </div>
                </div>
              )}

              {collectionFeedback && (
                <div
                  style={{
                    marginTop: '6px',
                    padding: '5px 8px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid #10b981',
                    borderRadius: '4px',
                    color: '#10b981',
                    fontSize: '0.76rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                  }}
                >
                  <i className="fi fi-rr-check" style={{ fontSize: '0.7rem' }} />
                  {collectionFeedback}
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '4px',
                  fontSize: '0.74rem',
                  color: 'var(--reader-text-subtle)',
                }}
              >
                <span>
                  {selectedCollectionIds.length === 0
                    ? 'Inbox (No collection selected)'
                    : selectedCollectionIds.length === 1
                    ? 'Adding to 1 collection'
                    : `Adding to ${selectedCollectionIds.length} collections`}
                </span>
              </div>
            </div>

            {/* Tags */}
            <div className="reader-form-group">
              <label className="reader-form-label">Tags (comma-separated)</label>
              <input
                type="text"
                className="reader-input"
                placeholder="tech, ai, finance, productivity"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            {/* Note */}
            <div className="reader-form-group">
              <label className="reader-form-label">Note / Takeaway (optional)</label>
              <textarea
                className="reader-input"
                style={{ height: '70px', padding: '8px 12px', resize: 'vertical' }}
                placeholder="Why are you saving this? Key summary..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="reader-modal-footer">
            <button
              type="button"
              className="reader-btn"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="reader-btn reader-btn-primary"
              disabled={isSubmitting || !url.trim()}
            >
              {isSubmitting ? 'Saving…' : 'Save to Library'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
